import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { getApiUrl } from '../config/api'
import '../App.css'

const slugify = (text = '') =>
  text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[\s\W-]+/g, '-')

function Home() {
  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filteredPages, setFilteredPages] = useState([])
  const [pageDisplayLimits, setPageDisplayLimits] = useState({})
  const searchResultsRef = useRef(null)

  useEffect(() => {
    const fetchPagesAndContents = async () => {
      setLoading(true)
      const pagesApiUrl = getApiUrl('pages')
      console.log('Fetching pages from:', pagesApiUrl)

      try {
        const response = await fetch(pagesApiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        })

        console.log('Pages response status:', response.status)

        if (!response.ok) {
          throw new Error(`Failed to load pages. Status: ${response.status}`)
        }

        const pagesJson = await response.json()
        const pagesList = Array.isArray(pagesJson) ? pagesJson : []

        const contentsResults = await Promise.all(
          pagesList.map(async (page) => {
            if (!page || !page.slug) {
              return []
            }

            const contentsUrl = getApiUrl(`pages/${page.slug}/contents`)
            try {
              const contentsResponse = await fetch(contentsUrl, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                },
              })

              if (!contentsResponse.ok) {
                console.warn(`Failed to fetch contents for page ${page.slug}. Status: ${contentsResponse.status}`)
                return []
              }

              const contentsJson = await contentsResponse.json()
              const rawContents = Array.isArray(contentsJson.contents) ? contentsJson.contents : []
              return rawContents.map((item) => ({
                ...item,
                slug: item?.slug || slugify(item?.title || ''),
              }))
            } catch (err) {
              console.error(`Error fetching contents for page ${page?.slug}:`, err)
              return []
            }
          })
        )

        const combinedPages = pagesList
          .map((page, index) => {
            if (!page) return null
            return {
              ...page,
              contents: contentsResults[index] || [],
            }
          })
          .filter(Boolean)

        setPages(combinedPages)
        setFilteredPages(combinedPages)

        const limits = {}
        combinedPages.forEach((page) => {
          if (page && page.id) {
            limits[page.id] = 20
          }
        })
        setPageDisplayLimits(limits)
      } catch (error) {
        console.error('Error fetching pages or contents:', error)
        setPages([])
        setFilteredPages([])
        setPageDisplayLimits({})
      } finally {
        setLoading(false)
      }
    }

    fetchPagesAndContents()
  }, [])

  // Icons array for different page types
  const getPageIcon = (index) => {
    const icons = [
      'fas fa-briefcase',
      'fas fa-chart-line',
      'fas fa-id-card',
      'fas fa-book',
      'fas fa-file-alt',
      'fas fa-graduation-cap',
      'fas fa-trophy',
      'fas fa-certificate'
    ]
    return icons[index % icons.length]
  }

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase()
    setSearchQuery(query)
    
    if (query === '') {
      setFilteredPages(pages)
    } else {
      const filtered = pages.reduce((acc, page) => {
        if (!page) return acc

        const pageTitle = (page.title || '').toLowerCase()
        const pageContents = Array.isArray(page.contents) ? page.contents : []
        const matchesTitle = pageTitle.includes(query)
        const matchingContents = pageContents.filter((content) =>
          (content?.title || '').toLowerCase().includes(query)
        )

        if (matchesTitle || matchingContents.length > 0) {
          acc.push({
            ...page,
            contents: matchesTitle ? pageContents : matchingContents,
          })
        }

        return acc
      }, [])
      setFilteredPages(filtered)
    }
  }
  
  // Handle View More for a specific page
  const handleViewMore = (pageId) => {
    setPageDisplayLimits(prev => ({
      ...prev,
      [pageId]: (prev[pageId] || 20) + 20
    }))
  }

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && searchQuery && filteredPages.length > 0) {
      e.preventDefault()
      setTimeout(() => {
        if (searchResultsRef.current) {
          searchResultsRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          })
        }
      }, 100)
    }
  }

  // Scroll to results when search changes
  useEffect(() => {
    if (searchQuery && filteredPages.length > 0 && searchResultsRef.current) {
      setTimeout(() => {
        searchResultsRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        })
      }, 100)
    }
  }, [searchQuery, filteredPages])

  return (
    <>
      {/* Search Bar */}
      <section className="search-section">
        <div className="container">
          <div className="page-search-container">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Search here..."
                value={searchQuery}
                onChange={handleSearch}
                onKeyPress={handleKeyPress}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="search-clear"
                  onClick={() => {
                    setSearchQuery('')
                    setFilteredPages(pages)
                    // Reset display limits
                    const limits = {}
                    pages.forEach(page => {
                      if (page && page.id) {
                        limits[page.id] = 20
                      }
                    })
                    setPageDisplayLimits(limits)
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-content">
            <h1>Your Gateway to Government Jobs & Career Success</h1>
            <p>Find the latest government job opportunities, check exam results, download admit cards, and enhance your skills with our comprehensive courses.</p>
            <div className="hero-buttons">
              {pages.length > 0 && (
                <>
                  {pages[0].link && (pages[0].link.startsWith('http://') || pages[0].link.startsWith('https://')) ? (
                    <a href={pages[0].link} target="_blank" rel="noopener noreferrer" className="btn btn-primary">Browse {pages[0].title}</a>
                  ) : (
                    <Link to={pages[0].link || `/${pages[0].slug || ''}`} className="btn btn-primary">Browse {pages[0].title}</Link>
                  )}
                  {pages.length > 1 && (
                    pages[1].link && (pages[1].link.startsWith('http://') || pages[1].link.startsWith('https://')) ? (
                      <a href={pages[1].link} target="_blank" rel="noopener noreferrer" className="btn btn-outline">View {pages[1].title}</a>
                    ) : (
                      <Link to={pages[1].link || `/${pages[1].slug || ''}`} className="btn btn-outline">View {pages[1].title}</Link>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="updates-section" ref={searchResultsRef}>
        <div className="container">
          <h2 className="section-title">
            <i className="fas fa-clock"></i>
            Latest Updates
          </h2>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Loading...</p>
            </div>
          ) : filteredPages.length > 0 ? (
            <div className="updates-list-by-page">
              {filteredPages.map((page, pageIndex) => {
                if (!page || !page.id || !page.title) return null
                
                const pageSlug = page.slug || page.title.toLowerCase().replace(/\s+/g, '-')
                const pagePath = page.link || `/${pageSlug}`
                const displayLimit = pageDisplayLimits[page.id] || 20
                const pageContents = page.contents && Array.isArray(page.contents) ? page.contents : []
                const visibleContents = pageContents.slice(0, displayLimit)
                const hasMore = pageContents.length > displayLimit
                
                return (
                  <div key={page.id} className="page-updates-group">
                    <div className="page-updates-header">
                      <i className={getPageIcon(pageIndex)}></i>
                      <h3>{page.title}</h3>
                    </div>
                    
                    {visibleContents.length > 0 ? (
                      <>
                        <ul className="updates-list">
                          {visibleContents.map((content, contentIndex) => {
                            if (!content || !content.id || !content.title) return null
                            
                            // Check link_type: if 'content', link to content display page
                            // if 'direct', link to external URL
                            const linkType = content.link_type || 'content'
                            const isContentPage = linkType === 'content'
                            const isDirectLink = linkType === 'direct' && content.link
                            const contentSlug = content.slug || slugify(content.title)
                            
                            return (
                              <li key={content.id} className="update-list-item">
                                {isContentPage ? (
                                  <Link
                                    to={`/${pageSlug}/${contentSlug}`}
                                    state={{ contentId: content.id }}
                                    className="update-link"
                                  >
                                    {content.title}
                                  </Link>
                                ) : isDirectLink ? (
                                  <a href={content.link} target="_blank" rel="noopener noreferrer" className="update-link">
                                    {content.title}
                                  </a>
                                ) : (
                                  <span className="update-link" style={{ cursor: 'default' }}>
                                    {content.title}
                                  </span>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                        
                        {hasMore && (
                          <div style={{ textAlign: 'center', marginTop: '1.5rem', marginBottom: '2rem' }}>
                            <button 
                              className="btn btn-primary"
                              onClick={() => handleViewMore(page.id)}
                            >
                              View More
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                        <p>No updates available for {page.title}</p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : searchQuery ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>No results found for "{searchQuery}"</p>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>No updates available at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* WhatsApp Join Section */}
      <section className="whatsapp-section">
        <div className="container">
          <div className="whatsapp-box">
            <div className="whatsapp-icon">
              <i className="fab fa-whatsapp"></i>
            </div>
            <div className="whatsapp-content">
              <h3>WhatsApp Join</h3>
              <p>Stay updated with latest job notifications on WhatsApp</p>
            </div>
            <div className="whatsapp-button">
              <a
                href="https://whatsapp.com/channel/0029Vb6DMPn2kNFiBHlkIw1Q"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-whatsapp"
              >
                <i className="fab fa-whatsapp"></i> Join Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Home

